import { Upload } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { useLanguage } from '@/contexts/AppContext';
import { profileTranslations, avatarCharacters } from './profileTranslations';

interface AvatarSelectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAvatarSelect: (avatarUrl: string) => void;
  onUploadClick: () => void;
}

export function AvatarSelectionModal({
  open,
  onOpenChange,
  onAvatarSelect,
  onUploadClick,
}: AvatarSelectionModalProps) {
  const { language, locale } = useLanguage();
  const t = profileTranslations[locale];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[min(90vw,28rem)] bg-background border-border">
        <DialogHeader>
          <DialogTitle className="text-[min(5vw,1.25rem)] text-foreground">
            {t.changePhoto}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {language === 'ko'
              ? '프로필 사진을 업로드하거나 아바타를 선택하세요'
              : language === 'es'
                ? 'Sube una foto o selecciona un avatar'
                : 'Upload a photo or select an avatar'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-[min(4vw,1rem)]">
          {/* Upload Photo Button */}
          <button
            onClick={() => {
              onUploadClick();
              onOpenChange(false);
            }}
            className="w-full flex items-center gap-[min(3vw,0.75rem)] p-[min(4vw,1rem)] rounded-[min(3vw,0.75rem)] bg-gradient-to-r from-primary/10 to-primary-hover/10 hover:from-primary/20 hover:to-primary-hover/20 border border-primary/30 transition-all duration-200"
          >
            <div className="p-[min(2.5vw,0.625rem)] rounded-[min(2.5vw,0.625rem)] bg-primary/20">
              <Upload className="w-[min(5vw,1.25rem)] h-[min(5vw,1.25rem)] text-primary" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-[min(4vw,1rem)] font-medium text-foreground">
                {t.uploadPhoto}
              </p>
              <p className="text-[min(3vw,0.75rem)] text-muted-foreground">
                {t.selectFromGallery}
              </p>
            </div>
          </button>

          {/* Divider */}
          <div className="flex items-center gap-[min(3vw,0.75rem)]">
            <div className="flex-1 h-[1px] bg-border"></div>
            <span className="text-[min(3vw,0.75rem)] text-muted-foreground">
              {t.or}
            </span>
            <div className="flex-1 h-[1px] bg-border"></div>
          </div>

          {/* Avatar Characters */}
          <div className="space-y-[min(2vw,0.5rem)]">
            <p className="text-[min(3.5vw,0.875rem)] text-muted-foreground font-medium">
              {t.selectAvatar}
            </p>
            <div className="grid grid-cols-3 gap-[min(3vw,0.75rem)] max-h-[min(60vw,15rem)] overflow-y-auto p-1">
              {avatarCharacters.map((avatar) => (
                <button
                  key={avatar.id}
                  onClick={() => onAvatarSelect(avatar.url)}
                  className="aspect-square rounded-[min(3vw,0.75rem)] overflow-hidden ring-2 ring-transparent hover:ring-primary transition-all duration-200 hover:scale-105 active:scale-95"
                >
                  <img
                    src={avatar.url}
                    alt={avatar.name}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
